import { Suspense } from 'react';
import { FieldPath, SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { useToast } from '@givebutter/primitives/toast';
import { associateContact, makePointOfContact } from 'api/contacts/companies';
import { CreateContactParams, createContact } from 'api/contacts/single';
import * as ContactForm from 'assets/js/pages/contacts/ContactForm';
import * as CompanyForm from 'assets/js/pages/contacts/companies/CompanyForm';
import { transformCommunicationData } from 'assets/js/pages/contacts/hooks/useSaveContact';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import { SelectItemWithLabel } from 'components/select';
import { ContactType } from 'constants/contacts';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { useForceRender } from 'customHooks/useForceRender';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Contact, ContactEmailType, ContactPhoneType } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface AssociateContactDialogForm {
    selectedContact: SelectItemWithLabel<Contact>[];
    type: ContactType;
    title: string;
    pointOfContact: {
        firstName: string;
        lastName: string;
    };
    pointOfContactEmails: { value: string; isPrimary: boolean; type: ContactEmailType }[];
    pointOfContactPhones: { value: string; isPrimary: boolean; type: ContactPhoneType }[];
    addAsPointOfContact: boolean;
}

interface AssociateContactDialogProps extends Dialog.DialogProps {}

const DEFAULT_EMAILS = [
    { value: '', isPrimary: true, type: 'personal' },
] as AssociateContactDialogForm['pointOfContactEmails'];
const DEFAULT_PHONES = [
    { value: '', isPrimary: true, type: 'home' },
] as AssociateContactDialogForm['pointOfContactPhones'];

const AssociateContactDialog = ({ children, ...props }: AssociateContactDialogProps) => {
    const { key, forceRender } = useForceRender();
    const { accountId } = useCurrentAccount();
    const { data: company } = useCurrentContact();
    const { error, success } = useToast();
    const queryClient = useQueryClient();

    const methods = Form.useForm<AssociateContactDialogForm>({
        defaultValues: {
            addAsPointOfContact: false,
            selectedContact: [],
            pointOfContact: {
                firstName: '',
                lastName: '',
            },
            title: '',
            pointOfContactEmails: DEFAULT_EMAILS,
            pointOfContactPhones: DEFAULT_PHONES,
            type: ContactType.Individual,
        },
    });

    const { handleSubmit, watch, setValue, setError, reset, resetFormButton, control, path } =
        methods;

    const [rawSelectedContact, addAsPointOfContact] = watch([
        'selectedContact',
        'addAsPointOfContact',
    ]);

    const selectedContact = rawSelectedContact[0];
    const isEphemeralContact = selectedContact?.id === ContactForm.NewContactId;

    const { mutateAsync: addPointOfContact } = useMutation({
        mutationFn: (contactId: number) => {
            return makePointOfContact(accountId, company.id, {
                contact_id: contactId,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contacts(accountId),
                type: 'all',
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.companyContacts(accountId, company.id),
                type: 'all',
            });
            success('Point of contact updated successfully.');
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Failed to update point of contact.');
        },
    });

    const {
        mutate: associate,
        mutateAsync: associateAsync,
        isPending,
    } = useMutation({
        mutationFn: async (
            values: Pick<AssociateContactDialogForm, 'selectedContact' | 'addAsPointOfContact'>,
        ) => {
            const contactId = parseInt(values.selectedContact[0].id);
            const associateRequest = associateContact(accountId, company.id, {
                contact_id: contactId,
            });

            if (addAsPointOfContact) {
                await addPointOfContact(contactId);
            }

            return associateRequest;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contactById(accountId, company.id),
            });
            success('Contact associated successfully');
            reset();
            forceRender();
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Failed to associate contact');
            resetFormButton();
        },
    });

    const { mutateAsync: create } = useMutation({
        mutationFn: (values: AssociateContactDialogForm) => {
            const { pointOfContactEmails, pointOfContactPhones, title, pointOfContact } = values;

            return createContact(accountId, {
                emails: transformCommunicationData(pointOfContactEmails),
                phones: transformCommunicationData(pointOfContactPhones),
                title,
                type: ContactType.Individual,
                ...pointOfContact,
            } as CreateContactParams);
        },
        onError: (e: GivebutterAxiosError) => {
            const errors = e.response?.data.errors;
            if (errors) {
                Object.keys(errors).forEach(key => {
                    const normalizedKey =
                        {
                            first_name: 'firstName',
                            last_name: 'lastName',
                        }[key] || key;

                    if (normalizedKey) {
                        setError(normalizedKey as FieldPath<AssociateContactDialogForm>, {
                            message: errors[key][0],
                        });
                    }
                });
            }
            error(e.response?.data?.message ?? 'Failed to create contact');
            resetFormButton();
        },
    });

    const { mutate: createAndAssociate, isPending: isCreating } = useMutation({
        mutationFn: async (values: AssociateContactDialogForm) => {
            const { data } = await create(values);

            const selectedContact = [
                {
                    label: `${data.first_name} ${data.last_name}`,
                    id: `${data.id}`,
                    value: data,
                },
            ];

            setValue('selectedContact', selectedContact);

            return associateAsync({
                selectedContact,
                addAsPointOfContact,
            });
        },
    });

    return (
        <Form.Provider key={key} {...methods}>
            <Dialog.Root {...props} onOpenChange={() => reset()}>
                {children}
                <Dialog.Portal>
                    <Dialog.Overlay />
                    <Dialog.Content className="flex flex-col gap-5">
                        <Dialog.Close />
                        <div>
                            <Dialog.Title>
                                <h3 className="text-lg font-medium">Add an associated contact</h3>
                            </Dialog.Title>
                            <p className="text-sm text-gray-600">
                                Add people associated to this company.
                            </p>
                        </div>
                        <form
                            onSubmit={handleSubmit(
                                (isEphemeralContact
                                    ? createAndAssociate
                                    : associate) as SubmitHandler<AssociateContactDialogForm>,
                            )}
                            className={'flex flex-col gap-8'}
                        >
                            <Suspense fallback={<Loader />}>
                                <div className="flex flex-col gap-4">
                                    <CompanyForm.PointOfContact>Contact</CompanyForm.PointOfContact>
                                    <Form.Container className="flex flex-row gap-2">
                                        <Form.UseFormCheckboxInput
                                            control={control}
                                            name={path('addAsPointOfContact')}
                                            className="mt-1"
                                        />
                                        <Form.Label name={path('addAsPointOfContact')}>
                                            Make point of contact
                                        </Form.Label>
                                    </Form.Container>
                                </div>
                                <div className="flex gap-4">
                                    <Dialog.Cancel className="flex-1" asChild>
                                        <Button variant="outline" color={'neutral'}>
                                            Cancel
                                        </Button>
                                    </Dialog.Cancel>
                                    <Form.SubmitButton
                                        className="flex-1"
                                        isSubmitting={isPending || isCreating}
                                    >
                                        Add
                                    </Form.SubmitButton>
                                </div>
                            </Suspense>
                        </form>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>
        </Form.Provider>
    );
};

const AssociateContactDialogTrigger = Dialog.Trigger;

const Root = AssociateContactDialog;
const Trigger = AssociateContactDialogTrigger;

export {
    AssociateContactDialog,
    AssociateContactDialogTrigger,
    //
    Root,
    Trigger,
};
